'use client'

import { useCurrentUser } from '@lastbrand/fe-common/universal-auth'
import { useEffect, useRef, useState } from 'react'

import { ChatProvider } from '@src/quince-site-integrated/src/integrations/chat-integration/chat-provider.types'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

export function useGladlyChat() {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
  const { user } = useCurrentUser()
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const { name, email } = user ?? {}
  // Use MutableRefObject by using any type
  const gladlyRef = useRef<typeof window.Gladly | null>(null)
  const [gladlyLoaded, setGladlyLoaded] = useState(false)

  useEffect(() => {
    let timer: NodeJS.Timeout | null = null

    timer = setInterval(() => {
      if (gladlyRef.current) {
        if (timer) clearInterval(timer)
        return
      }

      if (window.Gladly?.on) {
        // Use type assertion to override readonly
        gladlyRef.current = window.Gladly
        setGladlyLoaded(true)

        if (timer) clearInterval(timer)
      }
    }, 100)

    return () => {
      clearInterval(timer)
    }
  }, [])

  useEffect(() => {
    let unsubscribe: (() => void) | undefined = undefined
    if (gladlyLoaded && gladlyRef.current) {
      // eslint-disable-next-line i18next/no-literal-string, @typescript-eslint/no-unsafe-call
      unsubscribe = gladlyRef.current.on('sidekick:opened', async (properties: Record<string, unknown>) => {
        try {
          // eslint-disable-next-line i18next/no-literal-string
          console.info('Gladly sidekick:opened', { properties })
          await window.Gladly?.clearUser()
          window.Gladly?.setUser({ name, email })
          trackEvent(TELEMETRY_EVENT_NAMES.SUPPORT_CHAT_INITIATED, {
            provider: ChatProvider.GLADLY,
            trigger: 'widget_opened',
          })
        } catch (error) {
          console.error(error)
        }
      })
    }

    return () => {
      if (unsubscribe) {
        unsubscribe()
      }
    }
  }, [gladlyLoaded, email, name])

  const openChat = () => {
    try {
      window.Gladly?.show()
      trackEvent(TELEMETRY_EVENT_NAMES.CHAT_WITH_US_CLICKED, {})
    } catch (error) {
      console.error(error)
    }
  }

  return { openChat }
}
